import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Inbox,
  Package,
  Loader,
  CheckCircle2,
  Users,
  Languages,
  FileText,
  Receipt,
  MessageSquare,
  Flame,
  UserCheck,
  ClipboardList,
  ArrowRight,
  HelpCircle,
  RefreshCw,
  ArrowLeft,
  Eye,
  Pencil,
  Banknote,
  CreditCard,
  UserPlus,
  UserX,
  FileWarning,
  BarChart3,
  PieChart,
  Activity,
  Hourglass,
  SlidersHorizontal,
  RotateCcw,
  Calendar,
  MoreVertical,
  AlertTriangle,
  ArrowRightLeft,
  XCircle,
  User,
  Mail,
  Phone,
  History,
  Briefcase,
  Headphones,
  Landmark,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi, fileUrl } from '../../api/client';
import useAutoReload from '../../hooks/useAutoReload';
import StatusBadge from '../../components/dashboard/StatusBadge';
import DashModal from '../../components/dashboard/DashModal';
import AssignTranslatorForm from '../../components/dashboard/AssignTranslatorForm';
import DashToolbar from '../../components/dashboard/DashToolbar';
import ProfileForm from '../../components/dashboard/ProfileForm';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import { BarChart, HBarChart, DonutChart } from '../../components/dashboard/AdminCharts';
import { useCatalogLanguages } from '../../data/subServicesStore';
import { flagEmoji } from '../../utils/langFlags';
import '../../components/dashboard/DashForm.css';
import './AdminHome.css';
import './AdminTranslatorProfilePage.css';
import './AdminOrders.css';

function PriorityPill({ value, labels }) {
  const key = value || 'normal';
  return <span className={`dash-priority dash-priority--${key}`}>{labels?.[key] || key}</span>;
}

function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w.charAt(0)).join('').toUpperCase() || '·';
}

function Row({ label, children }) {
  return (
    <div className="od-row">
      <span className="od-label">{label}</span>
      <span className="od-value">{children}</span>
    </div>
  );
}

export function AdminHomePage() {
  const { t, lang } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);

  const reload = () => adminApi.overview().then(setData).catch(() => setData(null));

  useEffect(() => {
    reload();
  }, []);

  const stats = {
    orders: 0, users: 0, translators: 0, interpreters: 0, pending: 0,
    overdue: 0, dueSoon: 0, unpaid: 0, unpaidAmount: 0, revenue: 0,
    pipelineValue: 0, inProgress: 0, ready: 0, done: 0, newClients: 0,
    pendingApplications: 0, reviewsPending: 0, unassigned: 0,
    ...(data?.stats || {}),
  };

  const charts = data?.charts || {};
  const pendingQueue = data?.pendingQueue || [];
  const urgentOrders = data?.urgentOrders || [];
  const availableTranslators = data?.availableTranslators || [];
  const recentPayments = data?.recentPayments || [];
  const recentApplications = data?.recentApplications || [];
  const recentDocuments = data?.recentDocuments || [];
  const pendingReviews = data?.pendingReviews || [];

  const fmtMoney = (v) => {
    const n = Number(v) || 0;
    return n >= 10000 ? `${Math.round(n / 1000)}k` : `${n}`;
  };

  const fmtMonth = (key) => {
    const [y, m] = String(key || '').split('-').map(Number);
    if (!y || !m) return key;
    const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'short' });
  };

  const withMonthLabels = (rows) => (rows || []).map((r) => ({ ...r, label: fmtMonth(r.key) }));

  const kpisRow1 = [
    {
      icon: Loader, label: a.statInProgress, value: stats.inProgress,
      hint: `${a.statOrders}: ${stats.orders}`, cls: 'ah-kpi--teal',
    },
    {
      icon: Inbox, label: a.statPending, value: stats.pending,
      hint: a.kpiQueueHint, cls: 'ah-kpi--navy',
    },
    {
      icon: CheckCircle2, label: a.statDone, value: stats.done,
      hint: `${a.statReady}: ${stats.ready}`, cls: 'ah-kpi--green',
    },
    {
      icon: Banknote, label: a.statRevenue, value: `${stats.revenue} TND`,
      hint: a.hintRevenue, cls: 'ah-kpi--gold',
    },
  ];

  const kpisRow2 = [
    {
      icon: CreditCard, label: a.statPendingPayments, value: stats.unpaid,
      hint: `${stats.unpaidAmount} ${a.hintPaymentsAmount}`,
      cls: `ah-kpi--amber${stats.unpaid ? ' is-hot' : ''}`,
    },
    {
      icon: UserPlus, label: a.statNewClients, value: stats.newClients,
      hint: a.hintNewClients, cls: 'ah-kpi--indigo',
    },
    {
      icon: Languages, label: a.statActivePros, value: stats.translators,
      hint: `${stats.interpreters} ${a.hintInterpreters}`, cls: 'ah-kpi--violet',
    },
  ];

  const alerts = [
    {
      key: 'unassigned', icon: UserX, label: a.alertUnassigned,
      count: stats.unassigned, to: '/admin/orders', tone: 'danger',
    },
    {
      key: 'applications', icon: UserPlus, label: a.alertApplications,
      count: stats.pendingApplications, to: '/admin/translators', tone: 'warn',
    },
    {
      key: 'reviews', icon: FileWarning, label: a.alertReviews,
      count: stats.reviewsPending, to: '/admin/orders', tone: 'warn',
    },
    {
      key: 'payments', icon: CreditCard, label: a.alertPayments,
      count: stats.unpaid, to: '/admin/payments', tone: 'gold',
    },
    {
      key: 'dueSoon', icon: Hourglass, label: a.alertDueSoon,
      count: stats.dueSoon + stats.overdue, to: '/admin/orders', tone: 'danger',
    },
  ].filter((al) => al.count > 0);

  const chartCards = [
    {
      key: 'orders', icon: BarChart3, title: a.chartOrders,
      body: (charts.ordersByMonth || []).some((r) => r.count > 0)
        ? <BarChart data={withMonthLabels(charts.ordersByMonth)} valueKey="count" accent="var(--teal)" />
        : null,
    },
    {
      key: 'revenue', icon: Banknote, title: a.chartRevenue,
      body: (charts.revenueByMonth || []).some((r) => r.total > 0)
        ? <BarChart data={withMonthLabels(charts.revenueByMonth)} valueKey="total" format={(v) => fmtMoney(v)} accent="var(--gold)" />
        : null,
    },
    {
      key: 'services', icon: PieChart, title: a.chartServices,
      body: (charts.ordersByService || []).some((r) => r.count > 0)
        ? <HBarChart data={charts.ordersByService} />
        : null,
    },
    {
      key: 'categories', icon: PieChart, title: a.chartCategories,
      body: (charts.categorySplit || []).some((r) => r.count > 0)
        ? (
          <DonutChart
            data={charts.categorySplit}
            labels={{ translation: a.catTranslation, interpreting: a.catInterpreting }}
          />
        )
        : null,
    },
  ];

  return (
    <div className="ah">
      <header className="ah-hero">
        <div className="ah-hero-text">
          <span className="ah-hero-eyebrow">
            <ClipboardList size={13} />
            {a.title}
          </span>
          <h1>{a.homeTitle}</h1>
          <p>{a.homeSubtitle}</p>
        </div>
        <div className="ah-hero-actions">
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={reload}>
            <RefreshCw size={16} />
            {d.refresh || 'Actualiser'}
          </button>
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={() => setHowOpen(true)}>
            <HelpCircle size={16} />
            {d.howItWorks}
          </button>
        </div>
      </header>

      <div className="ah-kpis ah-kpis--row1">
        {kpisRow1.map((k) => (
          <div className={`ah-kpi ${k.cls}`.trim()} key={k.label}>
            <span className="ah-kpi-icon"><k.icon size={22} /></span>
            <div className="ah-kpi-body">
              <em>{k.label}</em>
              <strong>{k.value}</strong>
              <small>{k.hint}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="ah-kpis ah-kpis--row2">
        {kpisRow2.map((k) => (
          <div className={`ah-kpi ${k.cls}`.trim()} key={k.label}>
            <span className="ah-kpi-icon"><k.icon size={22} /></span>
            <div className="ah-kpi-body">
              <em>{k.label}</em>
              <strong>{k.value}</strong>
              <small>{k.hint}</small>
            </div>
          </div>
        ))}
      </div>

      <section className="ah-card">
        <div className="ah-card-head">
          <span className="ah-card-head-icon"><Flame size={16} /></span>
          <h2>{a.alertsTitle}</h2>
          <span className="ah-card-count">{alerts.length}</span>
        </div>
        <div className="ah-card-body">
          {alerts.length === 0 ? (
            <p className="ah-alerts-ok"><CheckCircle2 size={18} />{a.noAlerts}</p>
          ) : (
            <div className="ah-alerts">
              {alerts.map((al) => (
                <Link className={`ah-alert ah-alert--${al.tone}`} to={al.to} key={al.key}>
                  <span className="ah-alert-icon"><al.icon size={17} /></span>
                  <span>{al.label}</span>
                  <span className="ah-alert-count">{al.count}</span>
                  <ArrowRight size={15} className="ah-alert-go" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <h2 className="ah-section-title"><BarChart3 size={15} />{a.chartsTitle} · {a.last6Months}</h2>
      <div className="ah-charts">
        {chartCards.map((c) => (
          <section className="ah-card" key={c.key}>
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><c.icon size={16} /></span>
              <h2>{c.title}</h2>
            </div>
            <div className="ah-card-body">
              {c.body || <p className="ah-empty">{d.empty}</p>}
            </div>
          </section>
        ))}
      </div>

      <h2 className="ah-section-title"><Activity size={15} />{a.activityTitle}</h2>
      <div className="ah-activity">
        <section className="ah-card">
          <div className="ah-card-head">
            <span className="ah-card-head-icon"><CreditCard size={16} /></span>
            <h2>{a.recentPayments}</h2>
            <Link className="dash-link" to="/admin/payments">
              {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
            </Link>
          </div>
          <div className="ah-card-body">
            {recentPayments.length === 0 ? (
              <p className="ah-empty">{d.empty}</p>
            ) : (
              <div className="ah-queue">
                {recentPayments.map((p) => (
                  <div className="ah-mini-item" key={p.id}>
                    <div>
                      <strong>{p.orderCode || p.quoteCode || p.id}</strong>
                      <p>{p.client || '—'} · {p.createdAt || '—'}</p>
                    </div>
                    <div className="ah-queue-side">
                      <span className="ah-mini-amount">{p.amount} {p.currency}</span>
                      <StatusBadge status={p.status} labels={d.payment} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="ah-card">
          <div className="ah-card-head">
            <span className="ah-card-head-icon"><UserPlus size={16} /></span>
            <h2>{a.recentApplications}</h2>
            <Link className="dash-link" to="/admin/translators">
              {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
            </Link>
          </div>
          <div className="ah-card-body">
            {recentApplications.length === 0 ? (
              <p className="ah-empty">{d.empty}</p>
            ) : (
              <div className="ah-queue">
                {recentApplications.map((tr) => (
                  <div className="ah-mini-item" key={tr.id}>
                    <div>
                      <strong>{tr.name}</strong>
                      <p>{tr.langs}</p>
                    </div>
                    <Link className="dash-link" to={`/admin/translators/${tr.id}`}>{d.view}</Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="ah-card">
          <div className="ah-card-head">
            <span className="ah-card-head-icon"><FileText size={16} /></span>
            <h2>{a.recentDocuments}</h2>
            <Link className="dash-link" to="/admin/orders">
              {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
            </Link>
          </div>
          <div className="ah-card-body">
            {recentDocuments.length === 0 ? (
              <p className="ah-empty">{d.empty}</p>
            ) : (
              <div className="ah-queue">
                {recentDocuments.map((doc) => (
                  <div className="ah-mini-item" key={doc.id}>
                    <div>
                      <strong>{doc.name}</strong>
                      <p>{doc.orderCode} · {doc.uploadedAt ? new Date(doc.uploadedAt).toISOString().slice(0, 10) : '—'}</p>
                    </div>
                    {doc.url ? (
                      <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer">{d.view}</a>
                    ) : (
                      <Link className="dash-link" to={`/admin/orders/${doc.orderCode}`}>{d.view}</Link>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {pendingReviews.length > 0 && (
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><FileWarning size={16} /></span>
              <h2>{a.pendingReviewsTitle}</h2>
              <span className="ah-card-count">{pendingReviews.length}</span>
            </div>
            <div className="ah-card-body">
              <div className="ah-queue">
                {pendingReviews.map((r) => (
                  <div className="ah-mini-item" key={r.id}>
                    <div>
                      <strong>{r.document}</strong>
                      <p>{r.clientName} · {r.deadline || '—'}{r.verifier ? ` · ${r.verifier}` : ''}</p>
                    </div>
                    {r.orderCode ? (
                      <Link className="dash-link" to={`/admin/orders/${r.orderCode}`}>{d.view}</Link>
                    ) : (
                      <StatusBadge status={r.status} labels={d.status} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      <div className="ah-grid">
        {pendingQueue.length > 0 && (
          <div className="ah-col">
            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><Inbox size={16} /></span>
                <h2>{a.pendingQueue}</h2>
                <span className="ah-card-count">{pendingQueue.length}</span>
              </div>
              <div className="ah-card-body">
                <div className="ah-queue">
                  {pendingQueue.map((o) => (
                    <div className="ah-queue-item" key={o.id}>
                      <div>
                        <strong>{o.id} · {o.document}</strong>
                        <p>{o.client || '—'} · {o.sourceLang} → {o.targetLang}</p>
                      </div>
                      <div className="ah-queue-side">
                        <PriorityPill value={o.priority} labels={d.priority} />
                        <button type="button" className="dash-btn-sm" onClick={() => setAssignOrder(o)}>
                          {d.assign}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        <div className="ah-col ah-col-right">
          {urgentOrders.length > 0 && (
            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><Flame size={16} /></span>
                <h2>{a.urgentTitle}</h2>
                <span className="ah-card-count">{urgentOrders.length}</span>
              </div>
              <div className="ah-card-body">
                <div className="ah-queue">
                  {urgentOrders.map((o) => (
                    <div className="ah-queue-item" key={o.id}>
                      <div>
                        <strong>{o.id}</strong>
                        <p>
                          {o.deadline || '—'}
                          {o.isOverdue ? ` · ${a.overdueTag}` : o.isDueSoon ? ` · ${a.dueSoonTag}` : ''}
                        </p>
                      </div>
                      <Link className="dash-link" to={`/admin/orders/${o.id}`}>{d.view}</Link>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {availableTranslators.length > 0 && (
            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><UserCheck size={16} /></span>
                <h2>{a.availableTranslators}</h2>
                <span className="ah-card-count">{availableTranslators.length}</span>
              </div>
              <div className="ah-card-body">
                <div className="ah-queue">
                  {availableTranslators.map((tr) => (
                    <div className="ah-queue-item" key={tr.id}>
                      <div>
                        <strong>{tr.name}</strong>
                        <p>{tr.langs}</p>
                      </div>
                      <StatusBadge status={tr.status} labels={d.status} />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="ah-card ah-card--ops">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><ClipboardList size={16} /></span>
              <h2>{a.opsTitle}</h2>
            </div>
            <div className="ah-card-body">
              <div className="ah-shortcuts">
                <Link className="ah-shortcut" to="/admin/invoices"><Receipt size={17} />{a.navInvoices}</Link>
                <Link className="ah-shortcut" to="/admin/messages"><MessageSquare size={17} />{a.navMessages}</Link>
                <Link className="ah-shortcut" to="/admin/users"><Users size={17} />{a.navUsers}</Link>
                <Link className="ah-shortcut" to="/admin/translators"><Languages size={17} />{a.navTranslators}</Link>
                <Link className="ah-shortcut" to="/admin/orders"><Package size={17} />{a.navOrders}</Link>
              </div>
            </div>
          </section>
        </div>
      </div>

      {(data?.recentOrders || []).length > 0 && (
        <section className="ah-card">
          <div className="ah-card-head">
            <span className="ah-card-head-icon"><Package size={16} /></span>
            <h2>{a.recentOrders}</h2>
            <Link className="dash-link" to="/admin/orders">
              {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
            </Link>
          </div>
          <OrdersTable rows={data?.recentOrders || []} onAssign={setAssignOrder} onChanged={reload} />
        </section>
      )}

      <AssignTranslatorForm
        order={assignOrder}
        open={Boolean(assignOrder)}
        onClose={() => setAssignOrder(null)}
        onDone={reload}
      />

      <DashModal open={howOpen} title={d.assignHowTitle} onClose={() => setHowOpen(false)}>
        <ol style={{ margin: 0, paddingInlineStart: 18, color: 'var(--gray-700)', lineHeight: 1.7 }}>
          <li>Le client passe commande → statut <strong>translator_to_assign</strong>, sans paiement.</li>
          <li>La mission est proposée au meilleur professionnel compatible (langues, service, dispo) — ou à l’admin si aucun candidat.</li>
          <li>Le professionnel accepte → le client est invité à payer.</li>
          <li>Paiement reçu → la mission démarre dans `/pro`, suivi jusqu’à livraison.</li>
        </ol>
      </DashModal>
    </div>
  );
}

const ORDER_STATUSES = [
  'draft', 'request_sent', 'pending_review', 'quote_sent', 'quote_accepted', 'payment_pending',
  'paid', 'translator_to_assign', 'translator_assigned', 'translation_in_progress',
  'translation_submitted', 'verification_requested', 'lawyer_notary_in_progress',
  'correction_requested', 'new_version', 'verification_validated', 'final_control',
  'ready_to_deliver', 'delivered', 'completed', 'refused', 'cancelled', 'payment_failed',
  'quote_expired', 'translator_unavailable', 'pending', 'assigned', 'in_progress', 'ready',
];

function OrdersTable({ rows, onAssign, onChanged }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [menuFor, setMenuFor] = useState(null);
  const [menuPos, setMenuPos] = useState({});
  const [statusOrder, setStatusOrder] = useState(null);
  const [statusValue, setStatusValue] = useState('');

  useEffect(() => {
    if (menuFor == null) return undefined;
    const close = () => setMenuFor(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuFor]);

  if (!rows.length) return <p className="dash-empty">{d.empty}</p>;

  const menuOrder = rows.find((o) => o.id === menuFor);

  const toggleMenu = (e, o) => {
    e.stopPropagation();
    if (menuFor === o.id) {
      setMenuFor(null);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl';
    const openUp = r.bottom + 210 > window.innerHeight;
    setMenuPos({
      ...(rtl ? { left: Math.max(8, r.left) } : { right: Math.max(8, window.innerWidth - r.right) }),
      ...(openUp ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
    });
    setMenuFor(o.id);
  };

  const openStatus = (o) => {
    setMenuFor(null);
    setStatusOrder(o);
    setStatusValue(o.status || 'pending');
  };

  const saveStatus = async () => {
    if (!statusOrder) return;
    try {
      await adminApi.updateOrderMeta(statusOrder._id || statusOrder.id, { status: statusValue });
      setStatusOrder(null);
      onChanged?.();
    } catch { /* noop */ }
  };

  const cancelOrder = async (o) => {
    setMenuFor(null);
    if (!window.confirm(`Annuler la commande ${o.id} ?`)) return;
    try {
      await adminApi.updateOrderMeta(o._id || o.id, { status: 'cancelled' });
      onChanged?.();
    } catch { /* noop */ }
  };

  return (
    <>
      <div className="dash-table-wrap orders-table">
        <table className="dash-table">
          <thead>
            <tr>
              <th>{t.dashboard.client.colId}</th>
              <th>{a.colClient}</th>
              <th>{a.colService || 'Prestation'}</th>
              <th>{t.dashboard.client.colLangs}</th>
              <th>{a.colTranslator}</th>
              <th>{t.dashboard.client.colStatus}</th>
              <th>{a.colPayment}</th>
              <th>{a.colTotal}</th>
              <th>{a.colDate || 'Date'}</th>
              <th>{d.actions}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id}>
                <td>
                  <span className="o-id">{o.id}</span>
                  {o.isOverdue ? <div><span className="o-overdue">{a.overdueTag}</span></div> : null}
                </td>
                <td>
                  <span className="o-client">
                    {o.client ? <span className="o-avatar" aria-hidden="true">{initialsOf(o.client)}</span> : null}
                    <span className="o-client-name">{o.client || '—'}</span>
                  </span>
                </td>
                <td>
                  {o.category === 'interpreting' ? 'Interprétariat' : 'Traduction'}
                  {o.details?.translationService || o.details?.interpretingType
                    ? <div className="o-sub">{o.details.translationService || o.details.interpretingType}</div>
                    : null}
                </td>
                <td><strong>{o.langs}</strong></td>
                <td>
                  {o.translator
                    ? <span className="o-pro">{o.translator}</span>
                    : o.suggestedTranslator
                      ? <span className="suggestion-pill" title={a.suggestionTooltip || 'Proposition automatique'}>⚡ {o.suggestedTranslator}</span>
                      : <span className="o-unassigned"><AlertTriangle size={12} />À affecter</span>}
                </td>
                <td><StatusBadge status={o.status} labels={d.status} /></td>
                <td><StatusBadge status={o.paymentStatus || 'unpaid'} labels={d.payment} /></td>
                <td><span className="o-total">{o.total ?? o.price} TND</span></td>
                <td className="o-date">{o.date || '—'}</td>
                <td>
                  <div className="o-actions">
                    <Link className="dash-link o-view" to={`/admin/orders/${o.id}`}>
                      <Eye size={13} />{d.view}
                    </Link>
                    <button
                      type="button"
                      className={`o-menu-btn${menuFor === o.id ? ' is-open' : ''}`}
                      aria-label="Plus d'actions"
                      aria-expanded={menuFor === o.id}
                      aria-haspopup="menu"
                      onClick={(e) => toggleMenu(e, o)}
                    >
                      <MoreVertical size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {menuOrder && createPortal(
        <div
          className="o-menu-pop"
          role="menu"
          style={menuPos}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" role="menuitem" onClick={() => { setMenuFor(null); onAssign?.(menuOrder); }}>
            <UserCheck size={13} />{d.assign}
          </button>
          <button type="button" role="menuitem" onClick={() => { setMenuFor(null); navigate(`/admin/orders/${menuOrder.id}`); }}>
            <Pencil size={13} />Modifier
          </button>
          <button type="button" role="menuitem" onClick={() => openStatus(menuOrder)}>
            <ArrowRightLeft size={13} />Changer le statut
          </button>
          <div className="o-menu-sep" />
          <button type="button" role="menuitem" className="is-danger" onClick={() => cancelOrder(menuOrder)}>
            <XCircle size={13} />{d.status?.cancelled || 'Annuler'}
          </button>
        </div>,
        document.body
      )}

      <DashModal
        open={Boolean(statusOrder)}
        title={`Changer statut — ${statusOrder?.id || ''}`}
        onClose={() => setStatusOrder(null)}
      >
        <div className="dash-form">
          <div className="dash-field">
            <label>Statut</label>
            <select value={statusValue} onChange={(e) => setStatusValue(e.target.value)}>
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>{d.status[s] || s}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-primary" onClick={saveStatus}>{d.save}</button>
        </div>
      </DashModal>
    </>
  );
}

function OrderCard({ order: o, onAssign, transfer, onVerify }) {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const service = o.details?.translationService || o.details?.interpretingType;
  const prestation = o.category === 'interpreting' ? 'Interprétariat' : 'Traduction';
  return (
    <article className={`o-card${o.isOverdue ? ' is-overdue' : ''}`}>
      <div className="o-card-head">
        <span className="o-id">{o.id}</span>
        <StatusBadge status={o.status} labels={d.status} />
      </div>

      <ul className="o-card-rows">
        <li>
          <span className="o-avatar" aria-hidden="true">{initialsOf(o.client)}</span>
          <strong className="o-card-client">{o.client || '—'}</strong>
        </li>
        <li><Languages size={14} /><strong>{o.langs}</strong></li>
        <li><FileText size={14} /><span>{prestation}{service ? ` · ${service}` : ''}</span></li>
      </ul>

      <div className="o-card-sep" />

      <ul className="o-card-rows">
        <li>
          {o.translator
            ? <><UserCheck size={14} /><span className="o-pro">{o.translator}</span></>
            : o.suggestedTranslator
              ? <span className="suggestion-pill" title={a.suggestionTooltip || 'Proposition automatique'}>⚡ {o.suggestedTranslator}</span>
              : <span className="o-unassigned"><AlertTriangle size={12} />Aucun professionnel affecté</span>}
        </li>
        <li className="o-card-pay">
          <StatusBadge status={o.paymentStatus || 'unpaid'} labels={d.payment} />
          <span className="o-total">{o.total ?? o.price} TND</span>
        </li>
        {transfer && (
          <li
            className="o-card-transfer"
            style={{
              flexDirection: 'column', alignItems: 'stretch', gap: 4,
              background: '#fffbeb', border: '1px solid #fcd34d',
              borderRadius: 8, padding: '7px 9px',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600, color: '#92400e' }}>
              <Landmark size={12} /> Virement à vérifier — {transfer.amount} {transfer.currency || 'TND'}
            </span>
            <span style={{ fontSize: '0.75rem', color: '#92400e' }}>
              Réf. <code>{transfer.reference || `TAR-${o.id}`}</code>
            </span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className="dash-btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#047857', fontSize: '0.75rem' }}
                onClick={() => onVerify?.({ type: 'confirm', payment: transfer })}
              >
                <CheckCircle2 size={12} /> Confirmer le paiement
              </button>
              <button
                type="button"
                className="dash-btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b91c1c', fontSize: '0.75rem' }}
                onClick={() => onVerify?.({ type: 'reject', payment: transfer })}
              >
                <XCircle size={12} /> Non reçu
              </button>
            </span>
          </li>
        )}
        <li>
          <Calendar size={14} />
          <span className="o-date">{o.date || '—'}</span>
          {o.isOverdue ? <span className="o-overdue">{a.overdueTag}</span> : null}
        </li>
      </ul>

      <div className="o-card-foot">
        {!o.translator && (
          <button type="button" className="o-card-assign" onClick={() => onAssign?.(o)}>
            <UserCheck size={14} />{d.assign}
          </button>
        )}
        <Link className="o-card-view" to={`/admin/orders/${o.id}`}>
          {d.view}<ArrowRight size={13} />
        </Link>
      </div>
    </article>
  );
}

export function AdminOrdersPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [translatorFilter, setTranslatorFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [translators, setTranslators] = useState([]);
  const [bucket, setBucket] = useState('all');
  const [assignments, setAssignments] = useState(null);
  const [assignOrder, setAssignOrder] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [payVerify, setPayVerify] = useState(null); // { type: 'confirm'|'reject', payment }
  const [payBusy, setPayBusy] = useState(false);

  const reload = () => {
    adminApi.orders({
      status: statusFilter,
      paymentStatus: paymentFilter,
      category: categoryFilter,
      translatorId: translatorFilter,
      dateFrom,
      dateTo,
    }).then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
    adminApi.assignments().then(setAssignments).catch(() => {});
    adminApi.payments().then((res) =>
      setPendingTransfers(
        (res.payments || []).filter(
          (p) => p.status === 'pending' && ['transfer', 'virement', 'cash'].includes(p.method)
        )
      )
    ).catch(() => {});
  };

  useEffect(() => {
    adminApi.translators().then((res) => setTranslators(res.translators || [])).catch(() => setTranslators([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(reload, 250);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paymentFilter, categoryFilter, translatorFilter, dateFrom, dateTo]);

  useAutoReload(reload);

  useEffect(() => {
    let cancelled = false;
    adminApi.assignments()
      .then((res) => { if (!cancelled) setAssignments(res); })
      .catch(() => { if (!cancelled) setAssignments(null); });
    return () => { cancelled = true; };
  }, [bucket]);

  const bucketCounts = assignments?.counts || {};
  const bucketRows = bucket !== 'all' && bucket !== 'overdue' ? (assignments?.buckets?.[bucket] || []) : [];
  const overdueRows = orders.filter((o) => o.isOverdue);
  const unpaidCount = orders.filter((o) => (o.paymentStatus || 'unpaid') === 'unpaid').length;
  const listRows = [...(bucket === 'all' ? orders : bucket === 'overdue' ? overdueRows : bucketRows)]
    .sort((x, y) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(x.createdAt || 0) - new Date(y.createdAt || 0);
        case 'deadline': {
          const dx = x.deadline ? new Date(x.deadline).getTime() : Number.MAX_SAFE_INTEGER;
          const dy = y.deadline ? new Date(y.deadline).getTime() : Number.MAX_SAFE_INTEGER;
          return dx - dy;
        }
        case 'amountDesc':
          return (Number(y.total ?? y.price) || 0) - (Number(x.total ?? x.price) || 0);
        case 'amountAsc':
          return (Number(x.total ?? x.price) || 0) - (Number(y.total ?? y.price) || 0);
        case 'client':
          return String(x.client || '').localeCompare(String(y.client || ''), 'fr');
        default:
          return new Date(y.createdAt || 0) - new Date(x.createdAt || 0);
      }
    });
  const pg = usePagination(listRows, 12);
  const tableRows = pg.pageItems;
  const tableTotal = pg.total;

  const resetFilters = () => {
    setStatusFilter('all');
    setPaymentFilter('all');
    setCategoryFilter('all');
    setTranslatorFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortBy('recent');
  };

  const runPayVerify = async () => {
    if (!payVerify) return;
    setPayBusy(true);
    try {
      if (payVerify.type === 'confirm') await adminApi.confirmPayment(payVerify.payment._id);
      else await adminApi.rejectPayment(payVerify.payment._id);
      setPayVerify(null);
      reload();
    } catch {
      setPayVerify(null);
    } finally {
      setPayBusy(false);
    }
  };

  const transferByOrder = new Map(
    pendingTransfers.map((p) => [p.orderCode || p.orderId, p])
  );

  const statusOptions = [
    { id: 'all', label: a.filterAll || 'Tous' },
    { id: 'pending,request_sent,pending_review,translator_to_assign,payment_pending', label: d.status.pending || 'En attente' },
    { id: 'assigned,translator_assigned', label: d.status.assigned || 'Affectée' },
    { id: 'in_progress,translation_in_progress', label: d.status.in_progress || 'En cours' },
    { id: 'ready,translation_submitted,ready_to_deliver', label: d.status.ready || 'Prête' },
    { id: 'delivered', label: d.status.delivered || 'Livrée' },
    { id: 'completed', label: d.status.completed || 'Terminée' },
    { id: 'cancelled,refused,payment_failed', label: d.status.cancelled || 'Annulée' },
  ];

  const heroStats = [
    { id: 'all', icon: Package, label: a.assignAll || 'Toutes', value: orders.length },
    { id: 'pending', icon: Inbox, label: a.assignPending || 'En attente', value: bucketCounts.pending ?? '–' },
    { id: 'assigned', icon: UserCheck, label: a.assignAssigned || 'Affectées', value: bucketCounts.assigned ?? '–' },
    { id: 'reassign', icon: RefreshCw, label: a.assignReassign || 'À réaffecter', value: bucketCounts.reassign ?? '–' },
    { id: 'done', icon: CheckCircle2, label: a.assignDone || 'Terminées', value: bucketCounts.done ?? '–' },
    { id: 'overdue', icon: Flame, label: a.overdueTag || 'En retard', value: overdueRows.length, tone: 'danger' },
  ];

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Package size={22} /></span>
          <div className="orders-hero-text">
            <h1>{a.ordersTitle}</h1>
            <p>{a.ordersSubtitle}</p>
          </div>
        </div>
        <div className="orders-hero-stats" role="tablist">
          {heroStats.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={bucket === s.id}
              className={`orders-stat${s.tone ? ` orders-stat--${s.tone}` : ''}${bucket === s.id ? ' is-active' : ''}`}
              onClick={() => setBucket(s.id)}
            >
              <span className="orders-stat-icon"><s.icon size={15} /></span>
              <span className="orders-stat-body">
                <strong>{s.value}</strong>
                <em>{s.label}</em>
              </span>
            </button>
          ))}
        </div>
      </header>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            {unpaidCount > 0 && (
              <span className="of-unpaid">{unpaidCount} · {d.payment.unpaid}</span>
            )}
            <button type="button" className="of-reset" onClick={resetFilters}>
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>{d.statusLabel || 'Statut'}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {statusOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="of-field">
            <label>{a.colPayment || 'Paiement'}</label>
            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="all">{a.filterPaymentAll || 'Tous'}</option>
              <option value="unpaid">{d.payment.unpaid}</option>
              <option value="pending">{d.payment.pending}</option>
              <option value="paid">{d.payment.paid}</option>
              <option value="refunded">{d.payment.refunded}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{a.colService || 'Prestation'}</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="translation">Traduction</option>
              <option value="interpreting">Interprétariat</option>
            </select>
          </div>
          <div className="of-field">
            <label>{a.colTranslator || 'Professionnel'}</label>
            <select value={translatorFilter} onChange={(e) => setTranslatorFilter(e.target.value)}>
              <option value="all">Tous</option>
              {translators.map((tr) => (
                <option key={tr.id} value={tr.id}>{tr.name}</option>
              ))}
            </select>
          </div>
          <div className="of-field">
            <label>Du</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="of-field">
            <label>Au</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="of-field">
            <label>Trier par</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Plus récentes</option>
              <option value="oldest">Plus anciennes</option>
              <option value="deadline">Délai le plus proche</option>
              <option value="amountDesc">Montant décroissant</option>
              <option value="amountAsc">Montant croissant</option>
              <option value="client">Client (A → Z)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="orders-panel-head">
        <h2>{a.ordersTitle}</h2>
        <span className="orders-panel-count">{tableTotal}</span>
      </div>

      {tableRows.length === 0 ? (
        <p className="dash-empty">{d.empty}</p>
      ) : (
        <div className="orders-grid">
          {tableRows.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onAssign={setAssignOrder}
              transfer={transferByOrder.get(o.id)}
              onVerify={setPayVerify}
            />
          ))}
        </div>
      )}

      <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} onChange={pg.setPage} />

      <AssignTranslatorForm
        order={assignOrder}
        open={Boolean(assignOrder)}
        onClose={() => setAssignOrder(null)}
        onDone={reload}
      />

      <DashModal
        open={Boolean(payVerify)}
        title={payVerify?.type === 'confirm' ? 'Confirmer le paiement' : 'Virement non reçu'}
        subtitle={payVerify ? `${payVerify.payment.orderCode || ''} — ${payVerify.payment.amount} ${payVerify.payment.currency || 'TND'}` : ''}
        onClose={() => !payBusy && setPayVerify(null)}
      >
        {payVerify && (
          <div style={{ padding: '4px 2px' }}>
            {payVerify.type === 'confirm' ? (
              <p style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', fontSize: '0.88rem' }}>
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Confirmez uniquement si le crédit est <strong>effectif sur le relevé bancaire</strong>
                  {' '}(montant {payVerify.payment.amount} {payVerify.payment.currency || 'TND'},
                  référence <code>{payVerify.payment.reference || `TAR-${payVerify.payment.orderCode}`}</code>).
                  Une capture ou un justificatif du client ne suffit pas.
                </span>
              </p>
            ) : (
              <p style={{ fontSize: '0.9rem' }}>
                Le virement annoncé n’a pas été crédité ? Le paiement sera marqué <strong>échoué</strong>
                {' '}et le client sera notifié pour régler à nouveau sa commande.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button type="button" className="btn btn-outline" onClick={() => setPayVerify(null)} disabled={payBusy}>
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={payVerify.type === 'reject' ? { color: '#b91c1c', borderColor: '#fca5a5', background: '#fff' } : undefined}
                onClick={runPayVerify}
                disabled={payBusy}
              >
                {payBusy ? 'Traitement…' : payVerify.type === 'confirm' ? 'Crédit vérifié — confirmer' : 'Marquer non reçu'}
              </button>
            </div>
          </div>
        )}
      </DashModal>
    </>
  );
}

export function AdminUsersPage({ role }) {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(role || 'all');
  const [msg, setMsg] = useState('');

  useEffect(() => setRoleFilter(role || 'all'), [role]);

  const reload = () => adminApi.users().then((res) => setUsers(res.users || [])).catch(() => setUsers([]));

  useEffect(() => {
    reload();
  }, []);

  const roleLabel = { client: d.roleClient, translator: d.roleTranslator, admin: d.roleAdmin };
  const filtered = users.filter((u) => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [u.name, u.email, u.role].some((x) => String(x || '').toLowerCase().includes(q));
  });
  const usersPg = usePagination(filtered, 6);

  const toggleActive = async (u) => {
    if (u.role === 'admin') return;
    setMsg('');
    try {
      await adminApi.setUserActive(u.id, !u.isActive);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Error');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{a.usersTitle}</h1>
        <p>{a.usersSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <DashToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={a.searchUsers}
        filters={[
          { id: 'all', label: a.filterAll },
          { id: 'client', label: d.roleClient },
          { id: 'translator', label: d.roleTranslator },
          { id: 'admin', label: d.roleAdmin },
        ]}
        activeFilter={roleFilter}
        onFilter={setRoleFilter}
      />
      <section className="dash-panel">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{a.colName}</th>
                <th>{a.colEmail}</th>
                <th>{a.colRole}</th>
                <th>{d.status.active}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {usersPg.pageItems.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{roleLabel[u.role] || u.role}</td>
                  <td>{u.isActive !== false ? '✓' : '—'}</td>
                  <td>
                    {u.role !== 'admin' ? (
                      <button type="button" className="dash-btn-sm" onClick={() => toggleActive(u)}>
                        {u.isActive !== false ? a.deactivate : a.activate}
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={usersPg.page} totalPages={usersPg.totalPages} total={usersPg.total} onChange={usersPg.setPage} />
      </section>
    </>
  );
}

export function AdminClientsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 4000);
  };

  const toggleActive = async () => {
    if (!confirmToggle) return;
    const next = confirmToggle.isActive === false;
    setToggleBusy(true);
    try {
      await adminApi.setUserActive(confirmToggle.id, next);
      setClients((prev) => prev.map((c) => (c.id === confirmToggle.id ? { ...c, isActive: next } : c)));
      setConfirmToggle(null);
      showToast(next
        ? `Compte de ${confirmToggle.name} activé avec succès.`
        : `Compte de ${confirmToggle.name} désactivé avec succès.`);
    } catch (err) {
      showToast(err.data?.error || 'Erreur');
    } finally {
      setToggleBusy(false);
    }
  };

  const reload = () => {
    adminApi.users()
      .then((res) => setClients((res.users || []).filter((u) => u.role === 'client')))
      .catch(() => setClients([]));
    adminApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  };

  useEffect(() => {
    reload();
  }, []);

  const statsByClient = {};
  orders.forEach((o) => {
    if (!o.clientId) return;
    if (!statsByClient[o.clientId]) statsByClient[o.clientId] = { count: 0, spent: 0 };
    statsByClient[o.clientId].count += 1;
    if (o.paymentStatus === 'paid') statsByClient[o.clientId].spent += Number(o.total ?? o.price) || 0;
  });

  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;
  const filtered = clients.filter((u) => {
    if (statusFilter === 'active' && u.isActive === false) return false;
    if (statusFilter === 'inactive' && u.isActive !== false) return false;
    if (dateFilter !== 'all') {
      const created = u.createdAt ? new Date(u.createdAt).getTime() : 0;
      if (!created || now - created > Number(dateFilter) * DAY) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [u.name, u.email, u.phone, u.city, u.country, u.organizationName]
      .some((x) => String(x || '').toLowerCase().includes(q));
  });
  const clientsPg = usePagination(filtered, 12);

  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');
  const clientTypeLabel = { particulier: 'Particulier', entreprise: 'Entreprise', organisation: 'Organisation' };

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Users size={22} /></span>
          <div className="orders-hero-text">
            <h1>{a.clientsTitle || 'Clients'}</h1>
            <p>{a.clientsSubtitle || 'Consulter et gérer les comptes clients et leur activité.'}</p>
          </div>
        </div>
      </header>

      {toast && (
        <div className="admin-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{toast}</span>
        </div>
      )}

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <button
              type="button"
              className="of-reset"
              onClick={() => { setSearch(''); setStatusFilter('all'); setDateFilter('all'); }}
            >
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={a.searchUsers || 'Rechercher un client…'}
            />
          </div>
          <div className="of-field">
            <label>Statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
          </div>
          <div className="of-field">
            <label>Date d'inscription</label>
            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="30">30 derniers jours</option>
              <option value="90">90 derniers jours</option>
              <option value="365">Cette année</option>
            </select>
          </div>
        </div>
      </div>

      <div className="orders-panel-head">
        <h2>{a.clientsTitle || 'Clients'}</h2>
        <span className="orders-panel-count">{filtered.length}</span>
      </div>

      {clientsPg.pageItems.length === 0 ? (
        <p className="dash-empty">{a.noClients || 'Aucun client.'}</p>
      ) : (
        <div className="orders-grid">
          {clientsPg.pageItems.map((u) => {
            const st = statsByClient[u.id] || { count: 0, spent: 0 };
            return (
              <article className="o-card" key={u.id}>
                <div className="o-card-head">
                  <span className="o-client">
                    <span className="o-avatar" aria-hidden="true">{initialsOf(u.name)}</span>
                    <span className="o-card-client">
                      {u.name}
                      {u.clientType && u.clientType !== 'particulier' && (
                        <small className="o-card-sub">{u.organizationName || clientTypeLabel[u.clientType]}</small>
                      )}
                    </span>
                  </span>
                  <span className={`dash-badge dash-badge--${u.isActive !== false ? 'active' : 'cancelled'}`}>
                    {u.isActive !== false ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <ul className="o-card-rows">
                  <li><Mail size={14} /><span>{u.email}</span></li>
                  <li><Phone size={14} /><span>{u.phone || '—'}</span></li>
                </ul>
                <div className="o-card-sep" />
                <ul className="o-card-rows">
                  <li><Package size={14} /><span><strong>{st.count}</strong> commande{st.count > 1 ? 's' : ''}</span></li>
                  <li className="o-card-pay">
                    <span className="o-card-kv"><Banknote size={14} />Dépensé</span>
                    <span className="o-total">{st.spent} TND</span>
                  </li>
                  <li><Calendar size={14} /><span className="o-date">Inscrit le {fmtDate(u.createdAt)}</span></li>
                </ul>
                <div className="o-card-foot">
                  <button type="button" className="o-card-view" onClick={() => navigate(`/admin/clients/${u.id}`)}>
                    <Eye size={14} /> Profil
                  </button>
                  <button type="button" className="o-card-view" onClick={() => navigate(`/admin/clients/${u.id}?edit=1`)}>
                    <Pencil size={14} /> Modifier
                  </button>
                  <button
                    type="button"
                    className={`o-card-view ${u.isActive !== false ? 'o-card-view--danger' : 'o-card-view--success'}`}
                    onClick={() => setConfirmToggle(u)}
                  >
                    {u.isActive !== false ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={clientsPg.page} totalPages={clientsPg.totalPages} total={clientsPg.total} onChange={clientsPg.setPage} />

      <DashModal
        open={Boolean(confirmToggle)}
        title={confirmToggle?.isActive !== false ? 'Désactiver le compte' : 'Activer le compte'}
        subtitle={confirmToggle?.name || ''}
        onClose={() => setConfirmToggle(null)}
      >
        <div style={{ padding: '4px 2px' }}>
          <p style={{ margin: '0 0 18px', fontSize: '0.92rem', lineHeight: 1.6 }}>
            {confirmToggle?.isActive !== false
              ? `Voulez-vous désactiver le compte de ${confirmToggle?.name} ? Il ne pourra plus se connecter à la plateforme.`
              : `Voulez-vous activer le compte de ${confirmToggle?.name} ? Il pourra se connecter et accéder à son espace.`}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-outline" onClick={() => setConfirmToggle(null)} disabled={toggleBusy}>
              Annuler
            </button>
            <button
              type="button"
              className={`btn ${confirmToggle?.isActive !== false ? 'btn-danger' : 'btn-primary'}`}
              onClick={toggleActive}
              disabled={toggleBusy}
            >
              {toggleBusy ? 'En cours…' : (confirmToggle?.isActive !== false ? 'Désactiver' : 'Activer')}
            </button>
          </div>
        </div>
      </DashModal>
    </>
  );
}

const CLIENT_HISTORY_LABELS = {
  created: 'Commande créée',
  payment_received: 'Paiement reçu',
  payment_failed: 'Paiement échoué',
  matching_proposal: 'Matching automatique',
  assigned: 'Professionnel affecté',
  unassigned: 'Professionnel désaffecté',
  status_changed: 'Statut modifié',
  mission_assigned: 'Mission envoyée',
  mission_accepted: 'Mission acceptée',
  mission_refused: 'Mission refusée',
  mission_in_progress: 'Traduction commencée',
  mission_ready: 'Document déposé',
  mission_delivered: 'Document livré',
  mission_cancelled: 'Mission annulée',
  document_uploaded: 'Document déposé',
  document_added: 'Document ajouté',
  delivery_updated: 'Livraison mise à jour',
  verifier_assigned: 'Vérificateur affecté',
};

const DOC_KIND_LABELS = { source: 'Source', draft: 'Brouillon', final: 'Final' };

export function AdminClientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(searchParams.get('edit') === '1');
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 4000);
  };

  useEffect(() => {
    if (!id) return;
    setBusy(true);
    adminApi.clientDetail(id)
      .then(setData)
      .catch(() => setError(a.notFound || 'Client non trouvé'))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (busy) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--dash-muted)', padding: 24 }}>
        <Loader size={22} className="spin" />
        <span>Chargement du profil…</span>
      </div>
    );
  }

  const client = data?.client;
  if (error || !client) {
    return (
      <>
        <header className="dash-page-head">
          <button type="button" className="dash-link" onClick={() => navigate('/admin/clients')}>← Clients</button>
          <h1>{error || 'Client non trouvé'}</h1>
        </header>
      </>
    );
  }

  if (editing) {
    return (
      <ProfileForm
        space="client"
        user={client}
        hidePassword
        onBack={() => setEditing(false)}
        onSave={async (form) => {
          try {
            const res = await adminApi.updateUser(client.id, form);
            setData((prev) => ({ ...prev, client: res.user }));
            return { ok: true };
          } catch (err) {
            showToast(err.data?.error || 'Erreur lors de la mise à jour.');
            return { ok: false };
          }
        }}
      />
    );
  }

  const orders = data.orders || [];
  const payments = data.payments || [];
  const messages = data.messages || [];
  const stats = data.stats || {};
  const isActive = client.isActive !== false;
  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');
  const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
  const clientTypeLabel = { particulier: 'Particulier', entreprise: 'Entreprise', organisation: 'Organisation' };

  const documents = orders.flatMap((o) =>
    (o.documents || []).map((doc) => ({ ...doc, orderId: o.id }))
  );

  const timeline = [
    client.createdAt
      ? { at: client.createdAt, label: 'Inscription', detail: 'Création du compte client', by: 'Système' }
      : null,
    ...orders.flatMap((o) =>
      (o.history || []).map((h) => ({
        at: h.at,
        label: CLIENT_HISTORY_LABELS[h.action] || h.action,
        detail: `${o.id}${h.detail ? ` — ${h.detail}` : ''}`,
        by: h.by,
      }))
    ),
    ...payments.map((p) => ({
      at: p.createdAt,
      label: `Paiement ${p.id}`,
      detail: `${p.amount} ${p.currency || 'TND'}${p.orderCode ? ` · ${p.orderCode}` : ''}`,
      by: d.payment?.[p.status] || p.status,
    })),
  ]
    .filter(Boolean)
    .filter((x) => x.at)
    .sort((x, y) => new Date(y.at) - new Date(x.at))
    .slice(0, 20);

  const toggleActive = async () => {
    setToggleBusy(true);
    try {
      const next = !isActive;
      await adminApi.setUserActive(client.id, next);
      setData((prev) => ({ ...prev, client: { ...prev.client, isActive: next } }));
      setConfirmToggle(false);
      showToast(next
        ? `Compte de ${client.name} activé avec succès.`
        : `Compte de ${client.name} désactivé avec succès.`);
    } catch (err) {
      showToast(err.data?.error || 'Erreur');
    } finally {
      setToggleBusy(false);
    }
  };

  const kpis = [
    { icon: Package, label: 'Commandes', value: stats.orders ?? 0 },
    { icon: Loader, label: 'En cours', value: (stats.inProgress ?? 0) + (stats.pending ?? 0) },
    { icon: CheckCircle2, label: 'Terminées', value: stats.done ?? 0 },
    { icon: Banknote, label: 'Total dépensé', value: `${stats.totalSpent ?? 0} TND` },
  ];

  return (
    <div className="atp-page cp-page">
      <button type="button" className="atp-back" aria-label={a.back || 'Retour'} onClick={() => navigate('/admin/clients')}>
        <ArrowLeft size={20} />
      </button>

      {toast && (
        <div className="admin-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{toast}</span>
        </div>
      )}

      <header className="dash-page-head cp-hero">
        <div className="cp-hero-in">
          <span className="cp-avatar" aria-hidden="true">{initialsOf(client.name)}</span>
          <div className="cp-hero-main">
            <h1>{[client.civility, client.name].filter(Boolean).join(' ')}</h1>
            <p>{client.email}{client.phone ? ` · ${client.phone}` : ''}</p>
            <div className="cp-hero-badges">
              <span className={`cp-badge ${isActive ? 'cp-badge--ok' : 'cp-badge--off'}`}>
                {isActive ? 'Actif' : 'Inactif'}
              </span>
              <span className="cp-badge">{clientTypeLabel[client.clientType] || 'Client'}</span>
              {client.isGuest ? <span className="cp-badge">Invité</span> : null}
              <span className="cp-badge">Inscrit le {fmtDate(client.createdAt)}</span>
            </div>
          </div>
          <div className="cp-actions">
            <button type="button" className="cp-btn" onClick={() => setEditing(true)}>
              <Pencil size={13} />Modifier
            </button>
            <button
              type="button"
              className={`cp-btn ${isActive ? 'cp-btn--danger' : 'cp-btn--ok'}`}
              onClick={() => setConfirmToggle(true)}
            >
              {isActive ? <UserX size={13} /> : <UserCheck size={13} />}
              {isActive ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        </div>
      </header>

      <div className="cp-stats">
        {kpis.map((k) => (
          <div className="dash-stat cp-stat" key={k.label}>
            <span className="cp-stat-icon"><k.icon size={15} /></span>
            <span>{k.label}</span>
            <strong>{k.value}</strong>
          </div>
        ))}
      </div>

      <div className="od-grid">
        <div>
          <section className="dash-panel">
            <h2><User size={16} /> Informations personnelles</h2>
            <dl className="od-dl">
              <Row label="Nom">{[client.civility, client.name].filter(Boolean).join(' ') || '—'}</Row>
              <Row label="Email">{client.email}</Row>
              <Row label="Téléphone">{client.phone || '—'}</Row>
              <Row label="Type">{clientTypeLabel[client.clientType] || client.clientType || '—'}</Row>
              {client.organizationName ? <Row label="Organisation">{client.organizationName}</Row> : null}
              {client.taxId ? <Row label="Identifiant fiscal">{client.taxId}</Row> : null}
              <Row label="Inscrit le">{fmtDate(client.createdAt)}</Row>
              <Row label="Statut">{isActive ? 'Actif' : 'Inactif'}</Row>
            </dl>
          </section>

          <section className="dash-panel">
            <h2><Package size={16} /> Commandes ({orders.length})</h2>
            {orders.length ? (
              <div className="cp-orders">
                {orders.map((o) => (
                  <Link key={o.id} className="cp-order" to={`/admin/orders/${o.id}`}>
                    <span className="o-id">{o.id}</span>
                    <span className="cp-order-info">
                      <strong>
                        {o.category === 'interpreting' ? 'Interprétariat' : 'Traduction'}
                        {o.langs ? ` · ${o.langs}` : ''}
                      </strong>
                      <small>{o.date || '—'} · {o.total ?? o.price} TND</small>
                    </span>
                    <StatusBadge status={o.status} labels={d.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <p className="dash-empty">Aucune commande.</p>
            )}
          </section>
        </div>

        <div>
          <section className="dash-panel">
            <h2><CreditCard size={16} /> Paiements ({payments.length})</h2>
            {payments.length ? (
              <div className="cp-orders">
                {payments.map((p) => (
                  <div className="cp-order" key={p.id}>
                    <span className="o-id">{p.id}</span>
                    <span className="cp-order-info">
                      <strong>{p.amount} {p.currency || 'TND'}</strong>
                      <small>{p.orderCode ? `${p.orderCode} · ` : ''}{p.paidAt || p.createdAt || '—'}</small>
                    </span>
                    <StatusBadge status={p.status} labels={d.payment} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="dash-empty">Aucun paiement.</p>
            )}
          </section>

          <section className="dash-panel">
            <h2><FileText size={16} /> Documents ({documents.length})</h2>
            {documents.length ? (
              documents.map((doc) => (
                <div className="od-doc" key={doc.id || `${doc.orderId}-${doc.name}`}>
                  <span className="od-doc-icon"><FileText size={18} /></span>
                  <span className="od-doc-body">
                    <strong>{doc.name}</strong>
                    <small>{doc.orderId} · {DOC_KIND_LABELS[doc.kind] || doc.kind} · {fmtDateTime(doc.uploadedAt)}</small>
                  </span>
                  <span className="od-doc-actions">
                    <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer">Visualiser</a>
                  </span>
                </div>
              ))
            ) : (
              <p className="dash-empty">Aucun document.</p>
            )}
          </section>

          <section className="dash-panel">
            <h2><MessageSquare size={16} /> Messages ({messages.length})</h2>
            {messages.length ? (
              <div className="cp-orders">
                {messages.slice(0, 8).map((m) => (
                  <div className="cp-order cp-order--msg" key={m.id}>
                    <span className="cp-order-info">
                      <strong>{m.from?.name || '—'}</strong>
                      <small>{m.body}</small>
                    </span>
                    <span className="o-date">{fmtDateTime(m.createdAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dash-empty">Aucun message.</p>
            )}
          </section>

          <section className="dash-panel">
            <h2><History size={16} /> Historique</h2>
            {timeline.length ? (
              <ul className="od-timeline">
                {timeline.map((ev, i) => (
                  <li key={`${ev.at}-${i}`}>
                    <span className="ot-time">{fmtDateTime(ev.at)}</span>
                    <span className="ot-label">{ev.label}</span>
                    <span className="ot-by">{[ev.detail, ev.by].filter(Boolean).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dash-empty">Aucun événement.</p>
            )}
          </section>
        </div>
      </div>

      <DashModal
        open={confirmToggle}
        title={isActive ? 'Désactiver le compte' : 'Activer le compte'}
        subtitle={client.name}
        onClose={() => setConfirmToggle(false)}
      >
        <div style={{ padding: '4px 2px' }}>
          <p style={{ margin: '0 0 18px', fontSize: '0.92rem', lineHeight: 1.6 }}>
            {isActive
              ? `Voulez-vous désactiver le compte de ${client.name} ? Il ne pourra plus se connecter à la plateforme.`
              : `Voulez-vous activer le compte de ${client.name} ? Il pourra se connecter et accéder à son espace.`}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-outline" onClick={() => setConfirmToggle(false)} disabled={toggleBusy}>
              Annuler
            </button>
            <button
              type="button"
              className={`btn ${isActive ? 'btn-danger' : 'btn-primary'}`}
              onClick={toggleActive}
              disabled={toggleBusy}
            >
              {toggleBusy ? 'En cours…' : (isActive ? 'Désactiver' : 'Activer')}
            </button>
          </div>
        </div>
      </DashModal>
    </div>
  );
}

export function AdminTranslatorsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;
  const navigate = useNavigate();
  const [translators, setTranslators] = useState([]);
  const [search, setSearch] = useState('');
  const emptyFilters = {
    type: '',
    sourceLang: '',
    targetLang: '',
    domain: '',
    translationType: '',
    documentType: '',
    officialService: '',
    interpretingType: '',
    interpretingMission: '',
    interpretingMode: '',
    availability: '',
    statut: '',
  };
  const [filters, setFilters] = useState({ ...emptyFilters });
  const [createOpen, setCreateOpen] = useState(false);
  const [createFrameKey, setCreateFrameKey] = useState(0);
  const [createFrameLoading, setCreateFrameLoading] = useState(true);
  const [editFrameLoading, setEditFrameLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [createdAccount, setCreatedAccount] = useState(null);
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState('');
  const { languages: catalogLangs } = useCatalogLanguages();

  const reload = () => adminApi.translators().then((res) => setTranslators(res.translators || [])).catch(() => setTranslators([]));

  useEffect(() => {
    reload();
  }, []);

  // L'iframe du formulaire envoie 'translatorSaved' (création) ou 'translatorUpdated'
  // (édition : on garde le modal ouvert pour permettre d'autres modifications)
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data === 'translatorUpdated') {
        setEditTarget(null);
        reload();
      } else if (typeof e.data === 'object' && e.data?.type === 'translatorSaved') {
        setCreateOpen(false);
        setEditTarget(null);
        setCreatedAccount({ email: e.data.email, password: e.data.password });
        setCreateFrameLoading(true);
        setCreateFrameKey((k) => k + 1);
        reload();
      } else if (e.data === 'translatorSaved') {
        setCreateOpen(false);
        setEditTarget(null);
        reload();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const openCreate = () => {
    setCreateOpen(true);
  };

  const openEdit = (tr) => {
    setEditFrameLoading(true);
    setEditTarget(tr);
  };

  const getPairs = (tr) =>
    (tr.languageCombos?.length ? tr.languageCombos : tr.translatorProfile?.languageCombos || []);

  const filtered = translators.filter((tr) => {
    const p = tr.translatorProfile || {};
    const f = filters;
    const q = search.toLowerCase().trim();

    if (f.type) {
      if (f.type === 'translator' && !(p.professionalType === 'translator' || p.professionalType === 'both')) return false;
      if (f.type === 'interpreter' && !(p.professionalType === 'interpreter' || p.professionalType === 'both')) return false;
      if (f.type === 'both' && p.professionalType !== 'both') return false;
    }

    if (f.sourceLang || f.targetLang) {
      // Normalise codes (ar) et noms français (Arabe) vers le code
      const langNorm = (v) => {
        const s = String(v || '').trim().toLowerCase();
        const names = {
          arabe: 'ar', 'français': 'fr', francais: 'fr', anglais: 'en',
          espagnol: 'es', italien: 'it', allemand: 'de', russe: 'ru',
          portugais: 'pt', turc: 'tr', chinois: 'zh',
        };
        return names[s] || s;
      };
      const pairs = getPairs(tr);
      const hasLang = (v, side) =>
        pairs.some((c) => langNorm(c[side]) === langNorm(v));
      if (f.sourceLang && !hasLang(f.sourceLang, 'source')) return false;
      if (f.targetLang && !hasLang(f.targetLang, 'target')) return false;
    }

    if (f.domain && !(p.specialties || []).includes(f.domain) && !(p.expertiseAreas || []).includes(f.domain)) {
      return false;
    }

    if (f.availability) {
      const avail = tr.availabilityStatus || p.availabilityStatus || '';
      if (avail !== f.availability) return false;
    }

    if (f.translationType && !(p.translationServices || []).includes(f.translationType)) return false;

    if (f.documentType && !(p.documentTypes || []).includes(f.documentType)) return false;

    if (f.officialService && !(p.officialServices || []).includes(f.officialService)) return false;

    if (f.interpretingType && !(p.interpretingServices || []).includes(f.interpretingType)) return false;

    if (f.interpretingMission && !(p.interpretingMissionTypes || []).includes(f.interpretingMission)) return false;

    if (f.interpretingMode && !(p.interpretingModes || []).includes(f.interpretingMode)) return false;

    if (f.statut === 'pending' && tr.isActive !== false) return false;
    if (f.statut === 'validated' && tr.isActive === false) return false;
    if (['info_requested', 'suspended', 'rejected'].includes(f.statut) && (p.status || '') !== f.statut) return false;

    if (!q) return true;
    const pairs = getPairs(tr)
      .map((c) => `${c.source} ${c.target} ${c.level}`).join(' ');
    const hay = [
      tr.firstName,
      tr.lastName,
      tr.name,
      tr.email,
      tr.phone,
      p.professionalTitle,
      p.professionalType,
      p.nationality,
      p.city,
      p.country,
      p.address,
      p.bio,
      tr.langs,
      pairs,
      ...(p.translationServices || []),
      ...(p.interpretingServices || []),
      ...(p.specialties || []),
      ...(p.expertiseAreas || []),
      ...(p.skills || []),
      ...(p.tools || []),
    ].map((x) => String(x || '').toLowerCase());
    return hay.some((s) => s.includes(q));
  });
  const trPg = usePagination(filtered, 6);

  const SPECIALTIES = ['Général', 'Juridique', 'Administratif', 'Technique', 'Médical', 'Financier', 'Commercial', 'Marketing', 'Informatique', 'Scientifique', 'Touristique', 'Autre'];
  const TRANSLATION_TYPES = ['Traduction professionnelle', 'Traduction assermentée', 'Traduction certifiée'];
  const DOCUMENT_TYPES = ['Documents personnels', 'Documents d\u2019identité', 'Documents administratifs', 'État civil', 'Diplômes & certificats', 'Documents juridiques', 'Contrats', 'Documents commerciaux', 'Documents médicaux', 'Documents techniques', 'Documents scientifiques'];
  const OFFICIAL_SERVICES = ['Traduction certifiée', 'Traduction assermentée', 'Légalisation', 'Apostille'];
  const INTERPRETING_TYPES = ['Interprétation consécutive', 'Interprétation de liaison', 'Interprétation simultanée', 'Interprétation chuchotée', 'Interprétation judiciaire', 'Interprétation médicale', 'Interprétation de conférence', 'Autre'];
  const MISSION_TYPES = ['Réunion', 'Conférence', 'Tribunal', 'Rendez-vous médical', 'Rendez-vous administratif', 'Entreprise', 'Événement', 'Autre'];
  const INTERPRETING_MODES = ['Présentiel', 'Visioconférence', 'Téléphone'];

  const filterOptions = {
    // Langues actives du catalogue — repli sur la liste i18n hors-ligne
    languages: catalogLangs.length
      ? Object.fromEntries(catalogLangs.map((l) => [
          l.code.toLowerCase(),
          [flagEmoji(l.flag), l.nameFr || l.code].filter(Boolean).join(' '),
        ]))
      : (t.quoteForm?.languages || { ar: 'Arabe', fr: 'Français', en: 'Anglais' }),
    specialties: SPECIALTIES,
    translationTypes: TRANSLATION_TYPES,
    documentTypes: DOCUMENT_TYPES,
    officialServices: OFFICIAL_SERVICES,
    interpretingTypes: INTERPRETING_TYPES,
    interpretingMissionTypes: MISSION_TYPES,
    interpretingModes: INTERPRETING_MODES,
  };

  const confirmToggleActive = async () => {
    if (!confirmToggle) return;
    setToggleBusy(true);
    try {
      const next = !confirmToggle.isActive;
      await adminApi.setUserActive(confirmToggle.id, next);
      setConfirmToggle(null);
      setToast(next
        ? `Compte de ${confirmToggle.name} activé avec succès.`
        : `Compte de ${confirmToggle.name} désactivé avec succès.`);
      setTimeout(() => setToast(''), 4000);
      reload();
    } catch (err) {
      setMsg(err.data?.error || 'Erreur');
    } finally {
      setToggleBusy(false);
    }
  };

  const countType = (v) =>
    translators.filter((tr) => {
      const pt = tr.translatorProfile?.professionalType || 'translator';
      return pt === v || pt === 'both';
    }).length;

  const proStats = [
    { id: 'all', icon: Users, label: 'Tous', value: translators.length },
    { id: 'translator', icon: Languages, label: 'Traducteurs', value: countType('translator') },
    { id: 'interpreter', icon: Headphones, label: 'Interprètes', value: countType('interpreter') },
    { id: 'active', icon: UserCheck, label: 'Actifs', value: translators.filter((x) => x.isActive !== false).length },
  ];

  const applyStat = (id) => {
    const f = { ...emptyFilters };
    if (id === 'translator' || id === 'interpreter') f.type = id;
    if (id === 'active') f.statut = 'validated';
    setFilters(f);
    setSearch('');
  };
  const activeStat = filters.statut === 'validated' ? 'active'
    : filters.type === 'translator' ? 'translator'
    : filters.type === 'interpreter' ? 'interpreter'
    : 'all';

  const setF = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const resetAll = () => { setSearch(''); setFilters({ ...emptyFilters }); };
  const languageList = Object.entries(filterOptions.languages);
  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('fr-FR') : '—');

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Languages size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{a.translatorsTitle}</h1>
            <p>{a.translatorsSubtitle}</p>
          </div>
          <button type="button" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }} onClick={openCreate}>
            <UserPlus size={16} />{a.createTranslator || d.addTranslator}
          </button>
        </div>
        <div className="orders-hero-stats" role="tablist">
          {proStats.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={activeStat === s.id}
              className={`orders-stat${activeStat === s.id ? ' is-active' : ''}`}
              onClick={() => applyStat(s.id)}
            >
              <span className="orders-stat-icon"><s.icon size={15} /></span>
              <span className="orders-stat-body">
                <strong>{s.value}</strong>
                <em>{s.label}</em>
              </span>
            </button>
          ))}
        </div>
      </header>

      {msg && <p className="dash-toast">{msg}</p>}

      {toast && (
        <div className="admin-toast" role="status">
          <CheckCircle2 size={18} />
          <span>{toast}</span>
        </div>
      )}

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <span>{filtered.length} / {translators.length}</span>
            <button type="button" className="of-reset" onClick={resetAll}>
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Recherche</label>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Recherche : nom / prénom, email…" />
          </div>
          <div className="of-field">
            <label>Profil</label>
            <select value={filters.type} onChange={(e) => setF('type', e.target.value)}>
              <option value="">Tous les profils</option>
              <option value="translator">Traducteur</option>
              <option value="interpreter">Interprète</option>
              <option value="both">Traducteur + Interprète</option>
            </select>
          </div>
          <div className="of-field">
            <label>Langue source</label>
            <select value={filters.sourceLang} onChange={(e) => setF('sourceLang', e.target.value)}>
              <option value="">Toutes</option>
              {languageList.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </div>
          <div className="of-field">
            <label>Langue cible</label>
            <select value={filters.targetLang} onChange={(e) => setF('targetLang', e.target.value)}>
              <option value="">Toutes</option>
              {languageList.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          </div>
          <div className="of-field">
            <label>Domaine</label>
            <select value={filters.domain} onChange={(e) => setF('domain', e.target.value)}>
              <option value="">Tous</option>
              {filterOptions.specialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {filters.type === 'interpreter' ? (
            <>
              <div className="of-field">
                <label>Type d'interprétation</label>
                <select value={filters.interpretingType} onChange={(e) => setF('interpretingType', e.target.value)}>
                  <option value="">Tous</option>
                  {filterOptions.interpretingTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="of-field">
                <label>Type de mission</label>
                <select value={filters.interpretingMission} onChange={(e) => setF('interpretingMission', e.target.value)}>
                  <option value="">Tous</option>
                  {filterOptions.interpretingMissionTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="of-field">
                <label>Mode d'interprétation</label>
                <select value={filters.interpretingMode} onChange={(e) => setF('interpretingMode', e.target.value)}>
                  <option value="">Tous</option>
                  {filterOptions.interpretingModes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="of-field">
                <label>Type de traduction</label>
                <select value={filters.translationType} onChange={(e) => setF('translationType', e.target.value)}>
                  <option value="">Tous</option>
                  {filterOptions.translationTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="of-field">
                <label>Type de document</label>
                <select value={filters.documentType} onChange={(e) => setF('documentType', e.target.value)}>
                  <option value="">Tous</option>
                  {filterOptions.documentTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="of-field">
                <label>Services officiels</label>
                <select value={filters.officialService} onChange={(e) => setF('officialService', e.target.value)}>
                  <option value="">Tous</option>
                  {filterOptions.officialServices.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </>
          )}
          <div className="of-field">
            <label>Disponibilité</label>
            <select value={filters.availability} onChange={(e) => setF('availability', e.target.value)}>
              <option value="">Toutes</option>
              <option value="disponible">Disponible</option>
              <option value="partiellement_disponible">Partiellement disponible</option>
              <option value="indisponible">Indisponible</option>
            </select>
          </div>
          <div className="of-field">
            <label>Statut du compte</label>
            <select value={filters.statut} onChange={(e) => setF('statut', e.target.value)}>
              <option value="">Tous</option>
              <option value="pending">En attente</option>
              <option value="validated">Validé / Actif</option>
              <option value="info_requested">Informations complémentaires</option>
              <option value="suspended">Suspendu</option>
              <option value="rejected">Refusé</option>
            </select>
          </div>
        </div>
      </div>

      <div className="orders-panel-head">
        <h2>{a.translatorsTitle}</h2>
        <span className="orders-panel-count">{filtered.length}</span>
      </div>

      {trPg.pageItems.length === 0 ? (
        <p className="dash-empty">Aucun traducteur.</p>
      ) : (
        <div className="orders-grid translators-grid">
          {trPg.pageItems.map((tr) => {
            const pairs = tr.languageCombos?.length
              ? tr.languageCombos
              : tr.translatorProfile?.langPairs || [];
            const photo = fileUrl(tr.photoUrl || tr.translatorProfile?.photoUrl);
            return (
              <article className="o-card" key={tr.id}>
                <div className="o-card-head">
                  <span className="o-client">
                    <span className={`o-avatar${photo ? ' o-avatar--photo' : ''}`} aria-hidden="true">
                      {photo ? <img src={photo} alt="" /> : initialsOf(tr.name)}
                    </span>
                    <span className="o-card-client">
                      {tr.name}
                      <small className="o-card-sub">
                        {{
                          translator: 'Traducteur',
                          interpreter: 'Interprète',
                          both: 'Traducteur & Interprète',
                        }[tr.translatorProfile?.professionalType || 'translator']}
                      </small>
                    </span>
                  </span>
                  <span className="o-card-badges" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    {(tr.availability === 'unavailable' || tr.availabilityStatus === 'indisponible') && (
                      <span className="dash-badge dash-badge--cancelled">Indisponible</span>
                    )}
                    {(tr.availability === 'busy' || ['en_mission', 'partiellement_disponible'].includes(tr.availabilityStatus)) && (
                      <span className="dash-badge dash-badge--pending">Occupé</span>
                    )}
                    {tr.availabilityStatus === 'sur_demande' && tr.availability !== 'unavailable' && (
                      <span className="dash-badge dash-badge--pending">Sur demande</span>
                    )}
                    <span className={`dash-badge dash-badge--${tr.isActive !== false ? 'active' : 'cancelled'}`}>
                      {tr.isActive !== false ? 'Actif' : 'Inactif'}
                    </span>
                  </span>
                </div>
                <ul className="o-card-rows">
                  <li><Mail size={14} /><span>{tr.email}</span></li>
                  <li><Phone size={14} /><span>{tr.phone || '—'}</span></li>
                  <li>
                    <Languages size={14} />
                    <span>{pairs.length ? pairs.slice(0, 3).map((p) => `${p.source}→${p.target}`).join(' · ') : '—'}</span>
                  </li>
                </ul>
                <div className="o-card-sep" />
                <ul className="o-card-rows">
                  <li>
                    <Briefcase size={14} />
                    <span>
                      {tr.missions ? `${tr.missions} ${a.colMissionsDone || 'mission(s) réalisée(s)'}` : a.noMissions || 'Aucune mission réalisée'}
                      {tr.missionStats && (tr.missionStats.inProgress || tr.missionStats.cancelled || tr.missionStats.refused) ? (
                        <em style={{ fontStyle: 'normal', color: 'var(--gray-500)', fontSize: '0.72rem' }}>
                          {' · '}
                          {[
                            tr.missionStats.inProgress ? `${tr.missionStats.inProgress} en cours` : null,
                            tr.missionStats.cancelled ? `${tr.missionStats.cancelled} annulée(s)` : null,
                            tr.missionStats.refused ? `${tr.missionStats.refused} refusée(s)` : null,
                          ].filter(Boolean).join(' · ')}
                        </em>
                      ) : null}
                    </span>
                  </li>
                  <li><Calendar size={14} /><span className="o-date">Inscrit le {fmtDate(tr.createdAt)}</span></li>
                </ul>
                <div className="o-card-foot">
                  <button type="button" className="o-card-view" onClick={() => navigate(`/admin/translators/${tr.id}`)}>
                    <Eye size={14} /> Profil
                  </button>
                  <button type="button" className="o-card-view" onClick={() => openEdit(tr)}>
                    <Pencil size={14} /> Modifier
                  </button>
                  <button
                    type="button"
                    className={`o-card-view ${tr.isActive ? 'o-card-view--danger' : 'o-card-view--success'}`}
                    onClick={() => setConfirmToggle(tr)}
                  >
                    {tr.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={trPg.page} totalPages={trPg.totalPages} total={trPg.total} onChange={trPg.setPage} />


      <DashModal
        open={createOpen}
        wide
        fill
        keepMounted
        title={a.createTranslator || 'Ajouter un traducteur'}
        subtitle="Même formulaire que la page « Devenir professionnel »"
        onClose={() => { setCreateOpen(false); reload(); }}
      >
        <div className="dash-modal-frame">
          {createFrameLoading && (
            <div className="dash-modal-frame-loading" role="status">
              <Loader size={26} className="spin" />
              <span>Chargement du formulaire…</span>
            </div>
          )}
          <iframe
            key={createFrameKey}
            src="/devenir-traducteur?embed=1"
            title="Formulaire devenir professionnel"
            onLoad={() => setCreateFrameLoading(false)}
            style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
          />
        </div>
      </DashModal>

      <DashModal
        open={Boolean(editTarget)}
        wide
        fill
        title={`Modifier : ${editTarget?.name || ''}`}
        subtitle="Même formulaire que la page « Devenir professionnel »"
        onClose={() => { setEditTarget(null); reload(); }}
      >
        {editTarget && (
          <div className="dash-modal-frame">
            {editFrameLoading && (
              <div className="dash-modal-frame-loading" role="status">
                <Loader size={26} className="spin" />
                <span>Chargement du profil…</span>
              </div>
            )}
            <iframe
              key={editTarget.id}
              src={`/devenir-traducteur?embed=1&edit=${editTarget.id}`}
              title="Modifier le profil"
              onLoad={() => setEditFrameLoading(false)}
              style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
            />
          </div>
        )}
      </DashModal>

      <DashModal
        open={Boolean(confirmToggle)}
        title={confirmToggle?.isActive ? 'Désactiver le compte' : 'Activer le compte'}
        subtitle={confirmToggle?.name}
        onClose={() => setConfirmToggle(null)}
      >
        <div style={{ padding: '4px 2px' }}>
          <p style={{ margin: '0 0 18px', fontSize: '0.92rem', lineHeight: 1.6 }}>
            {confirmToggle?.isActive
              ? `Voulez-vous désactiver le compte de ${confirmToggle?.name} ? Il ne pourra plus se connecter à la plateforme.`
              : `Voulez-vous activer le compte de ${confirmToggle?.name} ? Il pourra se connecter et accéder à son espace professionnel.`}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-outline" onClick={() => setConfirmToggle(null)} disabled={toggleBusy}>
              Annuler
            </button>
            <button
              type="button"
              className={`btn ${confirmToggle?.isActive ? 'btn-danger' : 'btn-primary'}`}
              onClick={confirmToggleActive}
              disabled={toggleBusy}
            >
              {toggleBusy ? 'En cours…' : (confirmToggle?.isActive ? 'Désactiver' : 'Activer')}
            </button>
          </div>
        </div>
      </DashModal>

      <DashModal
        open={Boolean(createdAccount)}
        title="Compte créé"
        subtitle="Transmettez ces identifiants au traducteur"
        onClose={() => { setCreatedAccount(null); setCopied(false); }}
      >
        {createdAccount && (
          <div style={{ padding: '4px 2px' }}>
            <p style={{ margin: '0 0 12px', fontSize: '0.92rem', lineHeight: 1.6 }}>
              Le compte a été créé avec succès. Voici le mot de passe temporaire à communiquer au traducteur :
            </p>
            <div className="dform-field">
              <label>Email</label>
              <input readOnly value={createdAccount.email} onFocus={(e) => e.target.select()} />
            </div>
            <div className="dform-field">
              <label>Mot de passe temporaire</label>
              <input readOnly value={createdAccount.password} onFocus={(e) => e.target.select()} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  navigator.clipboard.writeText(`Email : ${createdAccount.email}\nMot de passe : ${createdAccount.password}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? 'Copié !' : 'Copier'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => { setCreatedAccount(null); setCopied(false); }}>
                Fermer
              </button>
            </div>
          </div>
        )}
      </DashModal>

    </>
  );
}


